import React, { useState, useEffect } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import { listGalleryImages } from '../../services/awsService';

// TODO: Add retry functionality for failed image loads
// TODO: Consider adding image caching with service worker

/**
 * ImageLoader component - handles fetching and loading gallery images from S3
 * Optimized to prevent unnecessary re-renders and API calls
 */
const ImageLoader = ({ onImagesLoaded }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);

  // Store callback in ref to avoid re-render loops
  // onImagesLoaded is an inline function from parent, so it changes every render
  const onImagesLoadedRef = React.useRef(onImagesLoaded);
  onImagesLoadedRef.current = onImagesLoaded;

  useEffect(() => {
    let isMounted = true;

    const fetchImages = async () => {
      console.log('[DEBUG ImageLoader] Starting to fetch images from S3...');

      try {
        setLoading(true);
        setError(null);

        const fetchedImages = await listGalleryImages();

        // Check if component is still mounted before updating state
        if (!isMounted) {
          console.log('[DEBUG ImageLoader] Component unmounted, skipping state update');
          return;
        }

        console.log('[DEBUG ImageLoader] Fetched images:', fetchedImages.length);

        // Sort images by last modified date (newest first)
        const sortedImages = fetchedImages.sort((a, b) => {
          return new Date(b.lastModified) - new Date(a.lastModified);
        });

        setImages(sortedImages);
        onImagesLoadedRef.current?.(sortedImages);

        console.log('[DEBUG ImageLoader] Images loaded successfully');
      } catch (err) {
        console.error('[DEBUG ImageLoader] Error loading images:', err);

        if (!isMounted) return;

        setError('Failed to load gallery images. Please try again later.');
        onImagesLoadedRef.current?.([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchImages();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []); // Run once on mount only

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading images...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading gallery images...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3">
        <Alert.Heading>
          <i className="bi bi-exclamation-triangle me-2"></i>
          Error Loading Images
        </Alert.Heading>
        <p className="mb-0">{error}</p>
      </Alert>
    );
  }

  if (images.length === 0) {
    return (
      <Alert variant="info" className="m-3">
        <Alert.Heading>
          <i className="bi bi-images me-2"></i>
          No Images Found
        </Alert.Heading>
        <p className="mb-0">There are currently no images in the gallery.</p>
      </Alert>
    );
  }

  // Component rendered successfully but is hidden
  // Images are passed to parent via callback
  return null;
};

export default ImageLoader;
